import { fromJS as ImmutableFromJs, List as ImmutableList } from 'immutable';
import Moment from 'moment';
import { formatLocalDate } from '../../../../Common/Utilities/DateUtilities';
import Configuration from '../../../../Common/Globals/Scripts/Configuration';

const criteriaSummaryLabels = [
    { label: 'Owner', key: 'owners' },
    { label: 'Rate card types', key: 'rateCardTypes' },
    { label: 'Customers', key: 'customers' },
    { label: 'Suppliers', key: 'suppliers' },
    { label: 'Sold to parties', key: 'soldToParties' },
    { label: 'Effective dates', key: 'effectiveDates' },
    { label: 'Activities', key: 'rateActivities' },
    { label: 'Charge types', key: 'rateChargeTypes' },
    { label: 'Products', key: 'rateProducts' },
    { label: 'From zones', key: 'fromZones' },
    { label: 'To zones', key: 'toZones' }
];

function getCriteriaDisplayOrder() {
    return criteriaSummaryLabels;
}

function getDefaultSearchCriteria() {
    const now = Moment();
    return {
        rateCardTypes: [],
        customers: [],
        suppliers: [],
        effectiveDates: [ { userInput: now.format(Configuration.uiDateFormat), validDate: now.format("YYYY-MM-DD") }, { userInput: "", validDate: null } ],
        soldToParties: [],
        owners: [],
        rateActivities: [],
        rateChargeTypes: [],
        rateProducts: [],
        fromZones: [],
        toZones: [],
        searchText: {
            rateCardTypes: '',
            customers: '',
            suppliers: '',
            soldToParties: '',
            rateActivities: '',
            rateChargeTypes: '',
            rateProducts: '',
            fromZones: '',
            toZones: '',
        }
    };
}

function generateSearchSummary(searchCriteria, options) {
    const searchCriteriaSummary = criteriaSummaryLabels
        .map(criterion => generateSearchCriterionSummary(criterion.label, searchCriteria.get(criterion.key), options.get(criterion.key)));

    return ImmutableFromJs(searchCriteriaSummary);
}

function generateDateCriterionSummary(label, from, to) {
    function generateDateRangeMessage(fromDate, toDate) {
        const from = fromDate.get('validDate');
        const to = toDate.get('validDate');
        if(!from && !to) {
            return [];
        }
        if(!from) {
            return [ `On or before ${formatLocalDate(to)}` ];
        }
        if(!to) {
            return [ `On or after ${formatLocalDate(from)}` ];
        }
        return [ `On or after ${formatLocalDate(from)} and on or before ${formatLocalDate(to)}` ];
    }

    const message = generateDateRangeMessage(from, to);

    return {
        label,
        all: message.length == 0,
        selectedValues: message
    }
}

function generateSearchCriterionSummary(label, criterion, options) {
    if(label === "Effective dates") {
        return generateDateCriterionSummary(label, criterion.get(0), criterion.get(1));
    }

    const allValuesAreSelected = criterion && options ? criterion.size === options.size : false;
    return {
        label: label,
        all: allValuesAreSelected,
        selectedValues: allValuesAreSelected ? ImmutableList() : criterion.map(value => value.get('name'))
    };
}

function mapUiSelectionToApiFormat(uiSelection, uiOptions) {
    const ui = uiSelection.toJS();
    const options = uiOptions.toJS();

    function extractValue(name, extractor) {
        const criteria = ui[name];
        const allValuesAreSelected = options[name].length === criteria.length;
        return {
            all: allValuesAreSelected,
            specifiedCriteria: allValuesAreSelected ? [] : criteria.map(extractor)
        };
    }

    function extractId(name) {
        return extractValue(name, v => v.id);
    }

    function extractBusiness(name) {
        return extractValue(name, v => ({ businessId: v.id }));
    }

    function extractDate(name) {
        return {
            startInclusive: ui[name][0].validDate,
            endInclusive: ui[name][1].validDate
        };
    }

    function extractValueWithNamedId(name, idPropertyName) {
        return extractValue(name, v => ({ [idPropertyName]: v.id }));
    }

    return [
        extractBusiness('owners'),
        extractValueWithNamedId('rateActivities', 'rateActivityId'),
        extractValueWithNamedId('rateChargeTypes', 'rateChargeTypeId'),
        extractValueWithNamedId('rateCardTypes', 'rateCardTypeId'),
        extractBusiness('customers'),
        extractBusiness('suppliers'),
        extractBusiness('soldToParties'),
        extractValueWithNamedId('rateProducts', 'rateProductId'),
        extractValueWithNamedId('fromZones', 'rateZoneId'),
        extractValueWithNamedId('toZones', 'rateZoneId'),
        extractDate('effectiveDates')
    ]
}

export { getDefaultSearchCriteria, generateSearchSummary, mapUiSelectionToApiFormat, getCriteriaDisplayOrder, generateDateCriterionSummary }
